import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Upload, Search, ExternalLink, Award } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: '',
  slug: '',
  logo_url: '',
  description: '',
  category: '',
  website: '',
  is_active: true,
  order: 0,
};

const CertificationFormDialog = ({ language, editData = null, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editData ? { ...empty, ...editData } : empty);
    }
  }, [editData, open]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, logo_url: res.data.url }));
      toast.success(language === 'sv' ? 'Logo uppladdad' : 'Logo uploaded');
    } catch {
      toast.error(language === 'sv' ? 'Uppladdning misslyckades' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.logo_url) {
      toast.error(language === 'sv' ? 'Namn och logo krävs' : 'Name and logo required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (editData) {
        await axios.put(`${API}/certifications/${editData.id}`, payload);
        toast.success(language === 'sv' ? 'Uppdaterad' : 'Updated');
      } else {
        await axios.post(`${API}/certifications`, payload);
        toast.success(language === 'sv' ? 'Skapad' : 'Created');
      }
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'sv' ? 'Fel' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editData ? (
          <Button variant="ghost" size="icon" data-testid={`edit-certification-${editData.id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
        ) : (
          <Button data-testid="new-certification-btn" className="bg-stone-900 text-white hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'sv' ? 'Ny märkning' : 'New certification'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {editData
              ? (language === 'sv' ? 'Redigera märkning' : 'Edit certification')
              : (language === 'sv' ? 'Ny märkning' : 'New certification')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{language === 'sv' ? 'Namn' : 'Name'} *</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="WRAS, CE, MID, KIWA..."
              data-testid="cert-name-input"
            />
          </div>
          <div>
            <Label>{language === 'sv' ? 'Logo' : 'Logo'} *</Label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/webp"
                onChange={handleUpload}
                className="hidden"
                id="cert-logo-upload"
                data-testid="cert-logo-upload"
              />
              <label
                htmlFor="cert-logo-upload"
                className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm"
              >
                <Upload className="w-4 h-4" />
                {uploading
                  ? (language === 'sv' ? 'Laddar upp...' : 'Uploading...')
                  : (language === 'sv' ? 'Ladda upp logo' : 'Upload logo')}
              </label>
              {form.logo_url && (
                <div className="w-14 h-14 bg-stone-50 border border-stone-200 flex items-center justify-center p-1">
                  <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
            <p className="text-xs text-stone-500 mt-1">SVG, PNG, JPG, WebP</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{language === 'sv' ? 'Kategori' : 'Category'}</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder={language === 'sv' ? 'Dricksvatten, El, Mätning...' : 'Drinking water, Electrical...'}
              />
            </div>
            <div>
              <Label>{language === 'sv' ? 'Ordning' : 'Order'}</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          </div>
          <div>
            <Label>{language === 'sv' ? 'Beskrivning' : 'Description'}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={language === 'sv' ? 'Kort beskrivning som visas som tooltip...' : 'Short tooltip description...'}
              rows={2}
            />
          </div>
          <div>
            <Label>{language === 'sv' ? 'Webbplats (länk till organ)' : 'Website (issuing body)'}</Label>
            <Input
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={form.is_active}
              onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              data-testid="cert-active-switch"
            />
            <Label>{language === 'sv' ? 'Aktiv (kan väljas på produkter)' : 'Active (selectable on products)'}</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="bg-stone-900 text-white">
              {submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CertificationsSection = ({ language }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/certifications`);
      setItems(res.data || []);
    } catch {
      toast.error(language === 'sv' ? 'Kunde inte hämta märkningar' : 'Could not load certifications');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort denna märkning?' : 'Delete this certification?')) return;
    try {
      await axios.delete(`${API}/certifications/${id}`);
      toast.success(language === 'sv' ? 'Borttagen' : 'Deleted');
      fetchItems();
    } catch {
      toast.error(language === 'sv' ? 'Kunde inte ta bort' : 'Could not delete');
    }
  };

  const toggleActive = async (cert) => {
    try {
      await axios.put(`${API}/certifications/${cert.id}`, { ...cert, is_active: !cert.is_active });
      fetchItems();
    } catch {
      toast.error(language === 'sv' ? 'Fel' : 'Error');
    }
  };

  const filtered = items.filter((c) => {
    if (filterActive === 'active' && !c.is_active) return false;
    if (filterActive === 'inactive' && c.is_active) return false;
    if (search.trim() && !c.name.toLowerCase().includes(search.toLowerCase()) && !(c.category || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="bg-white border border-stone-200" data-testid="certifications-section">
      <div className="p-4 border-b border-stone-200 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[300px]">
          <Award className="w-5 h-5 text-[#321a27]" />
          <h3 className="text-stone-900 font-semibold">
            {language === 'sv' ? 'Klassificeringar & märkningar' : 'Certifications & markings'}
          </h3>
          <span className="text-xs text-stone-400 ml-2">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'sv' ? 'Sök...' : 'Search...'}
              className="pl-9 w-48"
              data-testid="cert-search-input"
            />
          </div>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="border border-stone-300 text-sm px-3 py-2 bg-white"
          >
            <option value="all">{language === 'sv' ? 'Alla' : 'All'}</option>
            <option value="active">{language === 'sv' ? 'Aktiva' : 'Active'}</option>
            <option value="inactive">{language === 'sv' ? 'Inaktiva' : 'Inactive'}</option>
          </select>
          <CertificationFormDialog language={language} onSuccess={fetchItems} />
        </div>
      </div>

      <div className="p-4">
        {filtered.length === 0 ? (
          <p className="text-stone-500 text-center py-12 text-sm">
            {language === 'sv' ? 'Inga märkningar' : 'No certifications'}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`cert-card-${c.id}`}
                className={`border p-4 flex items-center gap-4 ${c.is_active ? 'border-stone-200' : 'border-stone-200 bg-stone-50 opacity-60'}`}
              >
                <div className="w-16 h-16 bg-white border border-stone-100 flex items-center justify-center p-1 flex-shrink-0">
                  <img src={c.logo_url} alt={c.name} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-stone-900 truncate">{c.name}</h4>
                    {!c.is_active && (
                      <span className="text-[10px] uppercase tracking-wider text-stone-400 border border-stone-300 px-1.5">
                        {language === 'sv' ? 'Inaktiv' : 'Inactive'}
                      </span>
                    )}
                  </div>
                  {c.category && <p className="text-xs text-stone-500 truncate">{c.category}</p>}
                  {c.description && <p className="text-xs text-stone-600 mt-1 line-clamp-2">{c.description}</p>}
                  {c.website && (
                    <a
                      href={c.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#321a27] inline-flex items-center gap-1 mt-1 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" /> {language === 'sv' ? 'Webbplats' : 'Website'}
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <Switch
                    checked={c.is_active}
                    onCheckedChange={() => toggleActive(c)}
                    data-testid={`cert-toggle-${c.id}`}
                  />
                  <CertificationFormDialog language={language} editData={c} onSuccess={fetchItems} />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(c.id)}
                    className="text-stone-500 hover:text-red-600"
                    data-testid={`delete-cert-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificationsSection;
